/**
 * What actually happened to each schedule after flex_schedule/action/arm/ or
 * /reboot/ (ENG-1968 / ENG-1969).
 *
 * The response is a list of messages. Since actuate_admin #2963, messages
 * about a flex schedule carry `flex_schedule_id` and `outcome`
 * (restarted | settings_only | armed | failed), and a reboot of a schedule
 * that isn't running only deploys settings. Items come back in the
 * serializer's order, not the request's, so match by id. Messages with no
 * flex_schedule_id (settings deploy, a refused reboot) are ignored; a selected
 * schedule with no outcome item wasn't done.
 *
 * Before #2963 there are no outcomes, so the old one-line message stands.
 */

type Outcome = "restarted" | "settings_only" | "armed" | "failed";

interface FlexActionMessage {
  flex_schedule_id?: number | null;
  outcome?: Outcome | null;
}

const LINES: Record<Outcome | "missing", string> = {
  armed: "Armed",
  restarted: "Restarted",
  settings_only:
    "Not running, so not restarted. The new settings apply at its next scheduled start",
  failed: "Failed. Please try again",
  missing: "Not done. Please check the schedule and try again",
};

export const summarizeFlexActionResults = (
  responseData: unknown,
  selected: { ids: number[]; names: string[] },
  endpointAction: "arm" | "reboot"
): string => {
  const outcomes = new Map<number, Outcome>();
  if (Array.isArray(responseData)) {
    (responseData as FlexActionMessage[]).forEach((item) => {
      if (item?.flex_schedule_id != null && item.outcome) {
        outcomes.set(Number(item.flex_schedule_id), item.outcome);
      }
    });
  }

  if (outcomes.size === 0) {
    return `The following schedules have been successfully ${
      endpointAction === "reboot" ? "restarted" : "armed"
    }: ${selected.names.join(", ")}`;
  }

  return selected.ids
    .map(
      (id, index) =>
        `${selected.names[index]}: ${LINES[outcomes.get(Number(id)) ?? "missing"]}.`
    )
    .join("\n");
};
